'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import StreamCard from './StreamCard';
import BgOrbs from './BgOrbs';
import { STREAM_FEED, StreamItem } from '../data/streamFeed';

/**
 * SCENE STREAM — слайд 01.
 *
 * Слева: SEO h1 (sr-only) + brand-label + display-заголовок + сабтекст + CTA "Много!".
 * Справа: чат "Избранное" в Telegram-стиле, сообщения снизу-вверх,
 *  каждое с bookmark-бейджем.
 *
 * BUGFIX: counter в локальной closure внутри useEffect (не в state-updater),
 *  иначе React Strict Mode задвоит обновления.
 */

const VISIBLE_COUNT = 5;
const INTERVAL_MS = 1800;

type StreamRow = StreamItem & { uid: string };

export default function SceneStream() {
  const reduced = useReducedMotion() ?? false;

  const [visible, setVisible] = useState<StreamRow[]>(() =>
    STREAM_FEED.slice(0, VISIBLE_COUNT).map((m, i) => ({ ...m, uid: `init-${i}` })),
  );

  useEffect(() => {
    if (reduced) return;
    let counter = VISIBLE_COUNT;
    const interval = setInterval(() => {
      const item = STREAM_FEED[counter % STREAM_FEED.length];
      const uid = `m-${counter}`;
      counter += 1;
      setVisible((cur) => [...cur.slice(1), { ...item, uid }]);
    }, INTERVAL_MS);
    return () => clearInterval(interval);
  }, [reduced]);

  const goToOverflow = useCallback(() => {
    document.getElementById('scene-overflow')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section
      id="scene-stream"
      className="relative min-h-screen w-full overflow-hidden bg-[#f3fcf0] flex items-center"
    >
      <BgOrbs variant="soft" />

      <div className="relative max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center px-5 sm:px-8 pt-24 pb-14 lg:py-24">
        {/* Левая колонка */}
        <div className="relative flex flex-col items-start gap-7 lg:gap-9">
          {/* SEO-h1: невидимый, содержит главные ключевые слова */}
          <h1 className="sr-only">
            Favbox — бот для разбора сохранённого в Telegram и VK
          </h1>

          {/* Brand recall — мелкая mono-подпись */}
          <span className="font-mono text-[11px] text-[#b7102a] uppercase tracking-[0.22em]">
            Favbox
          </span>

          {/* Визуальный заголовок */}
          <h2
            className="font-display font-bold text-[#161d16] leading-[0.98] tracking-[-0.02em] -mt-3"
            style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}
          >
            Сколько всего интересного<br />
            <span className="text-[#b7102a]">в вашем избранном?</span>
          </h2>

          <p className="font-body text-[#4a5a49] text-base sm:text-lg leading-relaxed max-w-[320px] lg:max-w-md">
            Каждый день что-то цепляет — фильм, рецепт, идея, ссылка. Сохраняешь, чтобы вернуться.
          </p>

          <button
            type="button"
            onClick={goToOverflow}
            className="group relative inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body"
            style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.45)' }}
          >
            <span>Много!</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            <span aria-hidden className="absolute inset-0 rounded-2xl bg-[#b7102a] opacity-0 group-hover:opacity-30 blur-xl transition-opacity -z-10" />
          </button>
        </div>

        {/* Правая колонка — чат "Избранное" с маскотом-overwhelmed на фоне */}
        <div className="relative">
          {/* МОБИЛЬНЫЙ маскот: выглядывает сверху из-за чата.
              Сидит ВНУТРИ правой колонки → не выпадает в левую и не перекрывает заголовок. */}
          <motion.div
            aria-hidden
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden absolute pointer-events-none"
            style={{ top: '-210px', right: '12px', width: '200px', zIndex: 5 }}
          >
            <motion.div
              animate={reduced ? {} : { y: [0, -4, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Image
                src="/landing/mascot-overwhelmed.webp"
                alt=""
                width={520}
                height={650}
                className="w-full h-auto"
                priority={false}
              />
            </motion.div>
          </motion.div>

          {/* ДЕСКТОПНЫЙ маскот-overwhelmed: крупный, поверх чата (z-11),
              отзеркален по горизонтали — смотрит на левую колонку. */}
          <motion.div
            aria-hidden
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block absolute pointer-events-none"
            style={{ top: '80px', left: '220px', right: '0px', width: '480px', zIndex: 11 }}
          >
            <motion.div
              animate={reduced ? {} : { y: [0, -4, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Image
                src="/landing/mascot-overwhelmed.webp"
                alt=""
                width={520}
                height={650}
                className="w-full h-auto -scale-x-100"
                priority={false}
              />
            </motion.div>
          </motion.div>

          <div
            aria-hidden
            className="absolute -inset-8 bg-gradient-to-br from-[#dce5d9]/40 via-[#e7f0e5]/30 to-transparent rounded-[2rem] blur-2xl"
          />

          <div
            className="relative z-10 bg-white/80 backdrop-blur-sm border border-[#dce5d9] rounded-[2rem] overflow-hidden mt-[15px] lg:mt-0"
            style={{ boxShadow: '0 32px 64px -24px rgba(20,40,20,0.18), inset 0 1px 0 rgba(255,255,255,0.8)' }}
          >
            {/* Chat header с bookmark-аватаром */}
            <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-[#e7f0e5] bg-white/60">
              <div
                className="w-10 h-10 rounded-2xl bg-[#b7102a] flex items-center justify-center text-white shrink-0"
                style={{ boxShadow: '0 6px 16px -4px rgba(183,16,42,0.4)' }}
                aria-hidden
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 3a2 2 0 0 0-2 2v17l9-5 9 5V5a2 2 0 0 0-2-2H5z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-body font-semibold text-[#161d16] text-[15px] leading-tight">
                  Избранное
                </div>
                <div className="font-body text-[#7a8a79] text-xs mt-0.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5a7558] animate-pulse" />
                  только что добавлено
                </div>
              </div>
            </div>

            {/* Сообщения снизу-вверх */}
            <div className="relative h-[380px] sm:h-[420px] overflow-hidden px-4 sm:px-5 py-4">
              <div className="absolute inset-x-4 sm:inset-x-5 inset-y-4 flex flex-col justify-end gap-3.5">
                <AnimatePresence initial={false} mode="popLayout">
                  {visible.map((row) => (
                    <motion.div
                      key={row.uid}
                      layout
                      initial={reduced ? false : { opacity: 0, y: 28, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -16, transition: { duration: 0.3 } }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <StreamCard item={row} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div
                aria-hidden
                className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white via-white/70 to-transparent pointer-events-none"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
